import axios from 'axios';
import React, { useState } from 'react';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';
import { performActionWithConfirmation } from './siteAdminUtils';

interface DepartmentInfo {
  name: string;
  parentPartnerName: string | null;
}

export function SiteAdminDepartmentDelete() {
  const [departmentId, setDepartmentId] = useState('');
  const [departmentInfo, setDepartmentInfo] = useState<DepartmentInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDepartment = async () => {
    if (!departmentId.trim()) return;

    setLoading(true);
    setError(null);
    setDepartmentInfo(null);

    try {
      const response = await axios.get(`/api/v3/departments/${departmentId}?include=parent`);
      const department = normalizeJSONAPIResponse(response.data);

      setDepartmentInfo({
        name: department.name,
        parentPartnerName: department.parent?.name || null,
      });
    } catch (err: any) {
      setError(err.response?.status === 404 ? 'Department not found' : `Error loading department: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteDepartment = async () => {
    await performActionWithConfirmation(
      `Are you sure you wish to permanently delete department ${departmentId}? This action CANNOT BE UNDONE.`,
      async () => {
        await axios.delete(`/api/v3/departments/${departmentId}`);
        window.alert('Department deleted successfully');
        setDepartmentId('');
        setDepartmentInfo(null);
      }
    );
  };

  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDepartmentId(e.target.value);
    setDepartmentInfo(null);
    setError(null);
  };

  const isLoadDisabled = !departmentId.trim() || loading;
  const isValidDepartment = departmentInfo && departmentInfo.parentPartnerName !== null;
  const isDeleteDisabled = !isValidDepartment;

  return (
    <div style={{ margin: '20px' }}>
      <h1>Department Delete Tool</h1>
      <p>Enter a department ID to permanently delete it. This action cannot be undone.</p>
      <p style={{ color: 'red', fontWeight: 'bold' }}>
        Note: You must first remove all partner users from the department's people page before you can delete the
        department.
      </p>
      <div style={{ marginTop: '20px' }}>
        <input
          type="text"
          placeholder="Department ID"
          value={departmentId}
          onChange={handleIdChange}
          style={{ width: '200px', padding: '5px', marginRight: '10px' }}
        />
        <button onClick={loadDepartment} disabled={isLoadDisabled}>
          {loading ? 'Loading...' : 'Load Department'}
        </button>
      </div>

      {error && <div style={{ marginTop: '15px', color: 'red' }}>{error}</div>}

      {departmentInfo && !isValidDepartment && (
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#ffcccc', borderRadius: '4px' }}>
          <p>
            <strong>Name:</strong> {departmentInfo.name}
          </p>
          <p style={{ color: 'red', fontWeight: 'bold' }}>THIS IS NOT A DEPARTMENT</p>
        </div>
      )}

      {departmentInfo && isValidDepartment && (
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <p>
            <strong>Department Name:</strong> {departmentInfo.name}
          </p>
          <p>
            <strong>Partner:</strong> {departmentInfo.parentPartnerName}
          </p>
          <button
            onClick={deleteDepartment}
            disabled={isDeleteDisabled}
            style={{
              marginTop: '10px',
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Delete Department
          </button>
        </div>
      )}
    </div>
  );
}
